import { useEffect, useMemo } from 'react';

import { Flex } from '@chakra-ui/react';
import {
  Dropdown,
  DropdownChip,
  DropdownContent,
  DropdownRadioGroup,
  DropdownRadioItem,
  DropdownTrigger,
} from '@mesh.ai/rabbit';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { useEngagementFilterSources } from 'screens/Engagement/LandingPageV2/hooks/dataHooks';

import { Tooltip } from 'uiToolkitV1/Tooltip';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import { setEngagementSurveysEngagementIntent } from '../slice/EngagementIntentSlice';

// truncate upto 40 characters
const truncateString = (str: string, num: number) => (str.length > num ? `${str.slice(0, num)}...` : str);

const EngagementSurveyDropdown = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { search } = useLocation();

  const searchParams = useMemo(() => new URLSearchParams(search), [search]);

  const { data, isLoading } = useEngagementFilterSources();

  const { source: surveys } = data ?? {};

  const onSurveyChange = (value: { uuid: string; displayName: string }) => {
    searchParams.set('surveyId', value.uuid);
    history.push({
      search: searchParams.toString(),
    });
  };

  const selectedValue = surveys?.find(({ uuid }) => uuid === searchParams.get('surveyId'));

  useEffect(() => {
    if (!selectedValue && surveys?.length) {
      searchParams.set('surveyId', surveys[0].uuid);
      history.push({
        search: searchParams.toString(),
      });
    }
  }, [history, searchParams, selectedValue, surveys]);

  useEffect(() => {
    if (!isLoading) {
      dispatch(
        setEngagementSurveysEngagementIntent({
          surveys,
        })
      );
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, surveys]);

  if (!selectedValue) {
    return null;
  }

  return (
    <Dropdown>
      <DropdownTrigger asChild>
        <DropdownChip className="chip-dropdown">
          <TextBodyMedium bold color="text.g2" className="group-data[state=open]:text-[#004F67]">
            Survey :
          </TextBodyMedium>
          <TextBodyMedium bold color="text.g1">
            {/* more than 25 characters are causing metafilters to take extra space vertically */}
            {truncateString(selectedValue?.displayName, 25) ?? ''}
          </TextBodyMedium>
        </DropdownChip>
      </DropdownTrigger>
      <DropdownContent
        align="start"
        style={{
          maxHeight: '280px',
          maxWidth: '100%',
        }}
      >
        <Flex
          flexDir="column"
          flexGrow={1}
          style={{
            maxHeight: '276px',
            maxWidth: '100%',
          }}
        >
          <DropdownRadioGroup
            value={selectedValue?.uuid}
            style={{
              flexGrow: 1,
              overflowY: 'auto',
            }}
          >
            {surveys?.map(({ uuid, displayName, ...survey }) => (
              <DropdownRadioItem
                key={uuid}
                value={uuid}
                onClick={() =>
                  onSurveyChange({
                    uuid,
                    displayName,
                    ...survey,
                  })
                }
              >
                <Tooltip label={displayName} isDisabled={displayName.length < 40}>
                  {truncateString(displayName, 40)}
                </Tooltip>
              </DropdownRadioItem>
            ))}
          </DropdownRadioGroup>
        </Flex>
      </DropdownContent>
    </Dropdown>
  );
};

export { EngagementSurveyDropdown };
